import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  TextareaAutosize,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Category, fetchCategories } from "../../app/services/CategoryService";
import { fetchSubCategory } from "../../app/services/subcatagoryservice";
import { toast } from "react-toastify";
import {
  fetchProductById,
  updateProduct,
} from "../../app/services/ProductService";
import { useNavigate, useParams } from "react-router-dom";
import { Option, UNIT_OPTIONS } from "../../app/api/unitOptions";
import { PACKAGING_OPTIONS } from "../../app/api/packagingOptions";

const UpdateProduct: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    productName: "",
    productBaseName: "",
    sku: "",
    mrp: "",
    price: "",
    hsnCode: "",
    weight: "",
    quantity: "",
    category_code: "",
    unit: "",
    packaging_type: "",
    gst: "",
    cess: "0",
    description: "",
    keywords: "",
    sub_category_id: "",
  });

  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState<Option[]>([]);
  const [packagingTypes, setPackagingTypes] = useState<Option[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [images, setImages] = useState<FileList | null>(null);
  const [subCategories, setSubCategories] = useState<any[]>([]);

  if (!id) {
    navigate("/not-found");
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImages(e.target.files);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // When category is changed, fetch subcategories
    if (name === "category_code") {
      getSubCategories(value);
      setFormData((prev) => ({
        ...prev,
        sub_category_id: "",
      }));
    }
  };

  const getCategories = async () => {
    try {
      const res = await fetchCategories();
      if ((res as any).status === "success") {
        setCategories((res as any).data);
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to fetch categories");
    }
  };

  const getSubCategories = async (categoryCode: string) => {
    try {
      const res = await fetchSubCategory();
      if (res.status === "success") {
        const filtered = res.data.filter(
          (sc: any) =>
            String(sc.category?.code) === String(categoryCode)
        );
        setSubCategories(filtered);
      } else {
        setSubCategories([]);
        toast.error("Failed to fetch subcategories");
      }
    } catch (err) {
      setSubCategories([]);
      toast.error("Error fetching subcategories");
    }
  };

  const handleReset = () => {
    setFormData({
      productName: "",
      productBaseName: "",
      sku: "",
      mrp: "",
      price: "",
      quantity: "",
      hsnCode: "",
      weight: "",
      category_code: "",
      unit: "",
      packaging_type: "",
      gst: "",
      cess: "0",
      description: "",
      keywords: "",
      sub_category_id: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.productName.trim())
      return toast.error("Product name is required");
    if (!formData.productBaseName.trim())
      return toast.error("Product base name is required");
    if (!formData.sku.trim()) return toast.error("SKU is required");
    if (!formData.mrp) return toast.error("MRP is required");
    if (!formData.hsnCode.trim()) return toast.error("HSN Code is required");
    if (!formData.weight.trim()) return toast.error("Weight is required");
    if (!formData.category_code) return toast.error("Please select Category");
    if (!formData.unit) return toast.error("Please select unit");
    if (!formData.packaging_type)
      return toast.error("Please select Packaging type");
    if (!formData.gst.trim()) return toast.error("GST is required");
    if (!formData.description.trim())
      return toast.error("Description is required");

    try {
      const data = new FormData();
      data.append("name", formData.productName);
      data.append("short_description", formData.productBaseName);
      data.append("sku", formData.sku);
      data.append("base_mrp", formData.mrp);
      data.append("base_price", formData.price);
      data.append("quantity", formData.quantity);
      data.append("hsn_code", formData.hsnCode);
      data.append("weight", formData.weight);
      data.append("category_id", formData.category_code);
      data.append("sub_category_id", formData.sub_category_id);
      data.append("unit", formData.unit);
      data.append("packaging_type", formData.packaging_type);
      data.append("tax", formData.gst);
      data.append("description", formData.description);
      data.append("keywords", formData.keywords);

      if (images) {
        Array.from(images).forEach((file) => data.append("files", file));
      }

      setLoading(true);
      const res = await updateProduct(data, Number(id));
      if ((res as any).status === "success") {
        toast.success("Product updated successfully");
        navigate("/catalog/product");
      } else {
        toast.error("Something went wrong");
      }

      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update product");
      setLoading(false);
    }
  };

  const fetchProduct = async (productId: number) => {
    try {
      const res = await fetchProductById(productId);
      if ((res as any).status === "success") {
        const product = (res as any).data;

        setFormData({
          productName: product.name || "",
          productBaseName: product.short_description || "",
          sku: product.sku || "",
          mrp: product.base_mrp || "",
          price: product.base_price || "",
          quantity: product.quantity || "",
          hsnCode: product.hsn_code || "",
          weight: product.weight || "",
          category_code: product.category_id
            ? String(product.category_id)
            : "",
          unit: product.unit || "",
          packaging_type: product.packaging_type || "",
          gst: product.tax || "",
          cess: product.cess || "0",
          description: product.description || "",
          keywords: product.keywords || "",
          sub_category_id: product.sub_category_id
            ? String(product.sub_category_id)
            : "",
        });

        // ✅ Fetch subcategories for the existing category
        if (product.category_id) {
          getSubCategories(product.category_id);
        }
      } else {
        toast.error("Failed to load product");
      }
    } catch (err) {
      toast.error("Error loading product");
    }
  };

  useEffect(() => {
    getCategories();
    setUnits(UNIT_OPTIONS);
    setPackagingTypes(PACKAGING_OPTIONS);
    if (id) fetchProduct(Number(id));
  }, [id]);

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Update Product
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Box display="flex" flexWrap="wrap" gap={3}>
          <Box
            flex={1}
            minWidth="300px"
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <TextField
              required
              label="Product Name"
              name="productName"
              value={formData.productName}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Short Description"
              name="productBaseName"
              value={formData.productBaseName}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              required
              label="SKU"
              name="sku"
              value={formData.sku}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              required
              label="Base MRP"
              name="mrp"
              value={formData.mrp}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              required
              label="Base Price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              required
              label="HSN Code"
              name="hsnCode"
              value={formData.hsnCode}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              required
              label="Weight"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          <Box
            flex={1}
            minWidth="300px"
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <TextField
              select
              required
              label="Category"
              name="category_code"
              value={formData.category_code}
              onChange={handleChange}
              fullWidth
            >
              {categories.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            {subCategories.length > 0 && (
              <TextField
                select
                required
                label="Sub Category"
                name="sub_category_id"
                value={formData.sub_category_id}
                onChange={handleChange}
                fullWidth
              >
                {subCategories.map((sc) => (
                  <MenuItem key={sc.code} value={sc.code}>
                    {sc.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              select
              required
              label="Packaging Type"
              name="packaging_type"
              value={formData.packaging_type}
              onChange={handleChange}
              fullWidth
            >
              {packagingTypes.map((p) => (
                <MenuItem key={p.id} value={p.name}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="GST"
              name="gst"
              value={formData.gst}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              select
              required
              label="Unit of Measurement"
              name="unit"
              value={formData.unit}
              onChange={handleChange}
              fullWidth
            >
              {units.map((u) => (
                <MenuItem key={u.id} value={u.name}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box>
          <TextareaAutosize
            minRows={4}
            name="description"
            placeholder="Description"
            style={{ width: "100%", padding: 10, fontFamily: "inherit" }}
            value={formData.description}
            onChange={handleChange}
            required
          />
        </Box>

        <Box>
          <Typography fontWeight={500} mb={1}>
            Upload Images
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
          >
            Upload
            <input
              hidden
              accept="image/*"
              multiple
              type="file"
              onChange={handleImageChange}
            />
          </Button>
        </Box>

        <TextField
          label="Keywords"
          name="keywords"
          value={formData.keywords}
          onChange={handleChange}
          placeholder="Type something and press enter..."
          fullWidth
        />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="contained" color="warning" onClick={handleReset}>
            Reset
          </Button>
          {loading ? (
            <Typography>Updating....</Typography>
          ) : (
            <Button variant="contained" type="submit" color="primary">
              Update
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UpdateProduct;
